import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  CX,
  CY,
  FACE_MAX,
  FACE_MIN,
  R_IN,
  R_MARKER,
  R_NEEDLE,
  R_OUT,
  STEP_ARROW,
  STEP_PAGE,
  STEP_SHIFT,
  VIEWBOX,
  pt,
  sector,
  valueFromPoint,
} from './geometry';
import { BULLSEYE, MID, OUTER, clamp100, dial10 } from '@shared/scoring';

export interface DialMarker {
  /** 0–100. */
  value: number;
  /** Two or three characters — initials, or "YOU". */
  label: string;
}

export interface DialProps {
  /** 0–100. Controlled. */
  value: number;
  /** Omit to render a read-only instrument (also sets aria-hidden). */
  onChange?: (value: number) => void;
  /** Spectrum poles, used in the accessible name so the number means something. */
  poles?: [string, string] | null;
  /** Reveal only: draws the three scoring bands. */
  target?: number | null;
  /** Reveal only: everyone's guesses, pinned to the face. */
  markers?: DialMarker[];
  /** Overrides the generated accessible name. */
  label?: string;
}

/**
 * The dial. Hand-built SVG, pointer events with capture, full ARIA slider
 * contract. Spec §10 — every decision here is settled and load-bearing.
 */
export function Dial({ value, onChange, poles, target = null, markers, label }: DialProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const draggingRef = useRef(false);
  const interactive = typeof onChange === 'function';

  // Ticks never change. Building 21 lines + 11 numerals on every pointermove
  // would be the one avoidable cost in a 60fps drag.
  const ticks = useMemo(() => {
    const out: React.ReactNode[] = [];
    for (let i = 0; i <= 100; i += 5) {
      const major = i % 10 === 0;
      const [x1, y1] = pt(R_OUT - (major ? 20 : 11), i);
      const [x2, y2] = pt(R_OUT - 2, i);
      out.push(
        <line
          key={`t${i}`}
          x1={x1.toFixed(1)}
          y1={y1.toFixed(1)}
          x2={x2.toFixed(1)}
          y2={y2.toFixed(1)}
          stroke="#0F1620"
          strokeOpacity={major ? 0.75 : 0.35}
          strokeWidth={major ? 2 : 1}
        />,
      );
      if (major) {
        const [nx, ny] = pt(R_OUT - 34, i);
        out.push(
          <text
            key={`n${i}`}
            x={nx.toFixed(1)}
            y={(ny + 5).toFixed(1)}
            textAnchor="middle"
            fill="#0F1620"
            fillOpacity={0.8}
            fontFamily="ui-monospace, Menlo, monospace"
            fontSize={15}
            stroke="var(--paper)"
            strokeWidth={3}
            paintOrder="stroke fill"
          >
            {i / 10}
          </text>,
        );
      }
    }
    // End rules at 0 and 100: the face runs past them, so the playable range
    // needs to stay legible.
    for (const v of [0, 100]) {
      const [ax, ay] = pt(R_IN, v);
      const [bx, by] = pt(R_OUT, v);
      out.push(
        <line
          key={`e${v}`}
          x1={ax.toFixed(1)}
          y1={ay.toFixed(1)}
          x2={bx.toFixed(1)}
          y2={by.toFixed(1)}
          stroke="#0F1620"
          strokeOpacity={0.55}
          strokeWidth={2}
        />,
      );
    }
    return out;
  }, []);

  const needle = pt(R_NEEDLE, clamp100(value));

  /**
   * Marker placement, in two steps.
   *
   * Every pill sits on ONE ring, outside the face, fanned apart from its
   * neighbours, with a leader line to the true value on the dial.
   *
   * Two rings inside the hub — the v1 approach — is worse in a way only an
   * image shows: the inner ring's arc is so short that the same 1.5-unit gap
   * between two guesses is drawn six times narrower there than on the outer
   * one, and the pill nearest the hub reads as furthest from the target even
   * when it is the closest. Outside the face one scale unit is 6.5 user units
   * wide, so a three-character pill needs only ~6 units of separation and five
   * guessers fit with room to spare.
   */
  const placedMarkers = useMemo(() => {
    if (!markers?.length) return [];
    const RING = R_MARKER;
    const ranked = markers
      .map((m, i) => ({ ...m, i }))
      .sort((a, b) => a.value - b.value || a.i - b.i)
      .map((m) => ({ ...m, ring: RING, shown: m.value }));

    for (const ring of [RING]) {
      const group = ranked;
      if (group.length < 2) continue;
      // Arc length of one scale unit at this radius, in user units.
      const perUnit = ring * ((1.8 * Math.PI) / 180);
      const widest = Math.max(...group.map((m) => Math.max(27, 11 + m.label.length * 9)));
      const minSep = (widest + 3) / perUnit;
      // Left-to-right pass, then push back inside the face if we ran off the end.
      for (let k = 1; k < group.length; k++) {
        group[k].shown = Math.max(group[k].shown, group[k - 1].shown + minSep);
      }
      const overflow = group[group.length - 1].shown - 100;
      if (overflow > 0) {
        for (let k = group.length - 1; k >= 0; k--) {
          group[k].shown -= overflow;
          if (k > 0 && group[k - 1].shown <= group[k].shown - minSep) break;
          if (k > 0) group[k - 1].shown = Math.min(group[k - 1].shown, group[k].shown - minSep);
        }
      }
      for (const m of group) m.shown = Math.max(0, Math.min(100, m.shown));
    }
    return ranked;
  }, [markers]);

  const set = useCallback(
    (v: number) => {
      onChange?.(clamp100(v));
    },
    [onChange],
  );

  const onPointerDown = useCallback(
    (ev: React.PointerEvent<SVGSVGElement>) => {
      if (!interactive || !svgRef.current) return;
      const v = valueFromPoint(svgRef.current, ev.clientX, ev.clientY);
      if (v === null) return;
      draggingRef.current = true;
      // Tapping anywhere in the dial sets the value — no drag required. WCAG
      // 2.2 SC 2.5.7 (Dragging Movements) depends on this staying true.
      try {
        svgRef.current.setPointerCapture(ev.pointerId);
      } catch {
        /* older engines: capture is a nicety, the drag still works */
      }
      set(v);
      ev.preventDefault();
    },
    [interactive, set],
  );

  const onPointerMove = useCallback(
    (ev: React.PointerEvent<SVGSVGElement>) => {
      if (!draggingRef.current || !svgRef.current) return;
      const v = valueFromPoint(svgRef.current, ev.clientX, ev.clientY);
      if (v !== null) set(v);
      ev.preventDefault();
    },
    [set],
  );

  const endDrag = useCallback((ev: React.PointerEvent<SVGSVGElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    try {
      svgRef.current?.releasePointerCapture(ev.pointerId);
    } catch {
      /* nothing to release */
    }
  }, []);

  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent<SVGSVGElement>) => {
      if (!interactive) return;
      const step = ev.shiftKey ? STEP_SHIFT : STEP_ARROW;
      switch (ev.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          set(value - step);
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          set(value + step);
          break;
        case 'PageDown':
          set(value - STEP_PAGE);
          break;
        case 'PageUp':
          set(value + STEP_PAGE);
          break;
        case 'Home':
          set(0);
          break;
        case 'End':
          set(100);
          break;
        default:
          return;
      }
      ev.preventDefault();
    },
    [interactive, set, value],
  );

  // Pointer capture can survive an unmount mid-drag (submit while dragging).
  useEffect(() => () => void (draggingRef.current = false), []);

  const accessibleName =
    label ??
    (poles
      ? `Your guess on the dial. 0 is ${poles[0]}, 10 is ${poles[1]}.`
      : 'Your guess on the dial, 0 to 10');

  const bandStroke = { stroke: '#0F1620', strokeOpacity: 0.55, strokeWidth: 1.5 };

  return (
    <svg
      ref={svgRef}
      className="dial"
      viewBox={VIEWBOX}
      role={interactive ? 'slider' : 'img'}
      aria-label={
        interactive ? accessibleName : label ? `${label}: ${dial10(value)} out of 10` : accessibleName
      }
      tabIndex={interactive ? 0 : undefined}
      aria-valuemin={interactive ? 0 : undefined}
      aria-valuemax={interactive ? 10 : undefined}
      aria-valuenow={interactive ? Number(dial10(value)) : undefined}
      aria-valuetext={interactive ? `${dial10(value)} out of 10` : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
    >
      {/* Face: runs past the scale ends so the 0 and 10 numerals sit on paper. */}
      <path d={sector(FACE_MIN, FACE_MAX, R_IN, R_OUT)} fill="var(--paper)" />
      <path
        d={sector(FACE_MIN, FACE_MAX, R_IN, R_OUT)}
        fill="none"
        stroke="#0F1620"
        strokeOpacity={0.35}
        strokeWidth={2}
      />

      {/* Scoring bands. Stroked at their boundaries: the fills alone sit at
          1.2–1.8:1 against the paper and would fail WCAG 1.4.11, and the band
          edges are what tell you which zone you landed in. */}
      {target !== null && (
        <g>
          <path
            d={sector(clamp100(target - OUTER), clamp100(target + OUTER), R_IN, R_OUT)}
            fill="var(--teal)"
            fillOpacity={0.55}
            {...bandStroke}
          />
          <path
            d={sector(clamp100(target - MID), clamp100(target + MID), R_IN, R_OUT)}
            fill="var(--amber)"
            fillOpacity={0.8}
            {...bandStroke}
          />
          <path
            d={sector(clamp100(target - BULLSEYE), clamp100(target + BULLSEYE), R_IN, R_OUT)}
            fill="var(--rose)"
            {...bandStroke}
          />
        </g>
      )}

      {/* Guess markers: width-fitting rounded pills, because "YOU" and
          two-letter initials overflow a circle. */}
      <g>
        {placedMarkers.map((m) => {
          const [px, py] = pt(m.ring, clamp100(m.shown));
          // The leader line always ends at the true value, however far the pill
          // was fanned to make room — and it starts at the pill's EDGE, or it
          // is hidden under the pill and the displacement looks like an error.
          const [lx, ly] = pt(R_OUT - 3, clamp100(m.value));
          const [ex, ey] = pt(m.ring - 14, clamp100(m.shown));
          const w = Math.max(27, 11 + m.label.length * 9);
          return (
            <g key={`${m.i}-${m.label}`}>
              <line
                x1={ex.toFixed(1)}
                y1={ey.toFixed(1)}
                x2={lx.toFixed(1)}
                y2={ly.toFixed(1)}
                stroke="var(--paper)"
                strokeWidth={2}
              />
              <rect
                x={(px - w / 2).toFixed(1)}
                y={(py - 13).toFixed(1)}
                width={w}
                height={26}
                rx={13}
                fill="var(--ink-2)"
                stroke="var(--paper)"
                strokeWidth={2}
              />
              <text
                x={px.toFixed(1)}
                y={(py + 4.5).toFixed(1)}
                textAnchor="middle"
                fill="var(--paper)"
                fontFamily="ui-monospace, Menlo, monospace"
                fontSize={13}
              >
                {m.label}
              </text>
            </g>
          );
        })}
      </g>

      {/* The needle. The shaft is --ink (13.7:1 on paper) and the amber tip
          carries an --ink stroke: amber alone is 1.6:1 on paper and would fail
          1.4.11. Do not drop that stroke. */}
      <g>
        {/* Casing first: the shaft crosses numerals and pills, and without a
            paper-coloured under-stroke it deletes them. */}
        <line
          x1={CX}
          y1={CY}
          x2={needle[0].toFixed(2)}
          y2={needle[1].toFixed(2)}
          stroke="var(--paper)"
          strokeWidth={9}
          strokeLinecap="round"
          opacity={0.9}
        />
        <line
          x1={CX}
          y1={CY}
          x2={needle[0].toFixed(2)}
          y2={needle[1].toFixed(2)}
          stroke="var(--ink)"
          strokeWidth={5}
          strokeLinecap="round"
        />
        <circle
          cx={needle[0].toFixed(2)}
          cy={needle[1].toFixed(2)}
          r={7}
          fill="var(--amber)"
          stroke="var(--ink)"
          strokeWidth={2.5}
        />
      </g>
      {/* Ticks and numerals last, above the needle: the shaft crosses them, and
          whichever is painted second wins. Painted first, the numeral at the
          needle's position disappears — which at the default 5.0 means the
          centre of the scale is missing from the first dial anyone sees. */}
      <g>{ticks}</g>

      <circle cx={CX} cy={CY} r={22} fill="var(--ink-2)" stroke="var(--paper)" strokeWidth={3} />
      <circle cx={CX} cy={CY} r={6} fill="var(--amber)" />
    </svg>
  );
}

/** The dial plus its two pole labels — the unit every screen actually places. */
export function DialFace(props: DialProps & { poles: [string, string] | null }) {
  const { poles } = props;
  return (
    <div className="dialwrap">
      <Dial {...props} />
      <div className="poles">
        <div className="pole">
          <b>0</b>
          <span>{poles?.[0] ?? ''}</span>
        </div>
        <div className="pole r">
          <b>10</b>
          <span>{poles?.[1] ?? ''}</span>
        </div>
      </div>
    </div>
  );
}
